import React, { useState, useCallback } from 'react';
import { endOfQuarter, isAfter, isBefore, startOfQuarter } from 'date-fns';
import { Box, Grid, Pressable, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import type { Locale } from '@atlassian/jira-common-constants/src/supported-locales.tsx';
import { formatWithLocale } from '@atlassian/jira-platform-utils-date-fns/src/main.tsx';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import { formatIsoLocalDate } from '../../../common/utils/date/date-format';
import { getInitialMonthPickerDate } from '../../../common/utils/date/date-picker';
import { YearSelector } from '../../../common/year-selector';
import type { CalendarProps, CalendarState } from '../../../types';

const getQuarterData = (locale: Locale) => {
	const today = new Date();
	const quarters = [];
	for (let i = 0; i < 12; i += 3) {
		quarters.push({
			months: [i, i + 1, i + 2],
			name: `${formatWithLocale(
				new Date(today.getFullYear(), i),
				'MMM',
				locale,
			)}-${formatWithLocale(new Date(today.getFullYear(), i + 2), 'MMM', locale)}`,
		});
	}
	return quarters;
};

export const QuarterPicker = (props: CalendarProps) => {
	const [date, setDate] = useState(getInitialMonthPickerDate(props));

	const { selectedDate, dateType, onSelect } = props;
	const { month, year, initialYear } = date;

	const locale = useLocale();

	const onDateChange = useCallback(
		(newDate: CalendarState) => {
			setDate(newDate);
			const newDateObject = new Date(newDate.year, newDate.month);
			const newDateInterval = {
				start: formatIsoLocalDate(startOfQuarter(newDateObject)),
				end: formatIsoLocalDate(endOfQuarter(newDateObject)),
			};
			onSelect(newDateInterval);
		},
		[onSelect],
	);
	const QUARTER_DATA = getQuarterData(locale);

	const isQuarterDisabled = useCallback(
		(isSelected: boolean, endQuarter: number) => {
			if (!selectedDate) {
				return false;
			}
			const currentDate = new Date(year, endQuarter);
			return (
				!isSelected &&
				(dateType === 'start'
					? isBefore(selectedDate, currentDate)
					: isAfter(selectedDate, currentDate))
			);
		},
		[dateType, selectedDate, year],
	);

	const onPreviousYear = useCallback(() => {
		setDate({ initialYear, month, year: year - 1 });
	}, [initialYear, month, year]);
	const onNextYear = useCallback(() => {
		setDate({ initialYear, month, year: year + 1 });
	}, [initialYear, month, year]);

	return (
		<Box xcss={quarterPickerWrapperStyles}>
			<YearSelector year={year} onPreviousYear={onPreviousYear} onNextYear={onNextYear} />
			<Grid templateColumns="repeat(2, 1fr)" gap="space.050" alignContent="stretch">
				{QUARTER_DATA.map((item) => {
					const startQuarter = item.months[0];
					const endQuarter = item.months[2];
					const isSelected = date.initialYear === year && item.months.includes(month);
					return (
						<Pressable
							key={startQuarter}
							xcss={[quarterButtonsStyles, isSelected && quarterButtonsSelectedStyles]}
							onClick={() => onDateChange({ initialYear, year, month: endQuarter })}
							isDisabled={isQuarterDisabled(isSelected, endQuarter)}
						>
							{item.name}
						</Pressable>
					);
				})}
			</Grid>
		</Box>
	);
};

const quarterButtonsStyles = xcss({
	borderRadius: token('space.050'),
	padding: 'space.200',
	fontWeight: token('font.weight.medium'),
	backgroundColor: 'color.background.neutral.subtle',
	paddingBlock: 'space.500',
});

const quarterButtonsSelectedStyles = xcss({
	backgroundColor: 'color.background.selected',
	color: 'color.text.selected',
});

const quarterPickerWrapperStyles = xcss({
	padding: 'space.200',
});
